import React, { useRef, useLayoutEffect, useEffect, useState, useCallback, type FC } from 'react';
import { type WatchQueryFetchPolicy } from '@apollo/client';
import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, Inline, Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { StaggeredEntrance } from '@atlaskit/motion';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ObservableSpinner } from '@confluence/infinite-scroll';

import {
	AccessRequestsTableRow,
	type MaybeStartBlockedAccessFlowForUser,
} from './AccessRequestsTableRow';
import { useIsScrolled } from './useIsScrolled';
import { AccessRequestsTableState } from './AccessRequestsTableState';
import { useAccessRequests } from './useAccessRequests';
import DarkEmptyState from './assets/DarkEmptyState.png';
import LightEmptyState from './assets/LightEmptyState.png';
import DarkError from './assets/DarkError.png';
import LightError from './assets/LightError.png';

const i18n = defineMessages({
	errorFirstLine: {
		id: 'access-requests.error-title-first-line',
		defaultMessage: "We couldn't load the requests.",
		description: 'The text displayed when there is an error fetching access requests',
	},
	errorSecondLine: {
		id: 'access-requests.error-title-second-line',
		defaultMessage: 'Try again later.',
		description: 'The text displayed when there is an error fetching access requests',
	},
	emptyStateTitle: {
		id: 'access-requests.empty-state-title',
		defaultMessage: 'All requests have been resolved',
		description: 'The text displayed when there are no access requests',
	},
});

export interface AccessRequestsTableProps {
	tryCacheFirst: boolean;
	isBlockedAccessFlowActive: boolean;
	maybeStartBlockedAccessFlowForUser: MaybeStartBlockedAccessFlowForUser;
	minLoadingHeight: number;
	source: string;
	onHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
}

function isDefined<T>(value: T | null | undefined): value is T {
	return value !== null && value !== undefined;
}

export const getBucketedCount = (count: number) => {
	if (count < 10) {
		return count;
	}
	if (count >= 100) {
		return '100+';
	}

	// For 10-99, round down to nearest 10 and add '+'
	return `${Math.floor(count / 10) * 10}+`;
};

/**
 * This container with "relative" positioning
 * is necessary for the top border on scroll to
 * correctly anchor itself
 */
const accessRequestsContainerStyles = css({
	position: 'relative',
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	maxHeight: '416px',
});

const tableStyles = xcss({
	margin: '0',
	padding: 'space.0',
	width: 'calc(100% + 48px)',
	left: 'space.negative.300',
	overflowY: 'scroll',
	position: 'relative',
	flexGrow: 1,
});

const borderDummyStyles = xcss({
	height: '1px',
	width: 'calc(100% + 48px)',
	position: 'absolute',
	borderTopWidth: 'border.width',
	borderTopStyle: 'solid',
	borderColor: 'color.border',
	top: '-1px',
	left: 'space.negative.300',
	transition: '0.12s opacity ease',
	opacity: 0,
});

const tableBorderTop = xcss({
	opacity: 1,
});

const paddingBoxStyles = xcss({
	minHeight: '24px',
	width: '100%',
});

const spinnerContainerStyles = xcss({
	paddingBottom: 'space.300',
	height: '100%',
	width: '100%',
});

export const AccessRequestsTable: FC<AccessRequestsTableProps> = ({
	tryCacheFirst,
	isBlockedAccessFlowActive,
	maybeStartBlockedAccessFlowForUser,
	minLoadingHeight,
	source,
	onHasUnsavedChanges,
}) => {
	const { formatMessage } = useIntl();
	const [fetchPolicy] = useState<WatchQueryFetchPolicy>(
		tryCacheFirst ? 'cache-first' : 'network-only',
	);

	const [shouldBlockRowProcessing, setShouldBlockRowProcessing] = useState(false);
	const onRowProcessingStart = useCallback(() => setShouldBlockRowProcessing(true), []);
	const onRowProcessingEnd = useCallback(() => setShouldBlockRowProcessing(false), []);
	const { requests, endCursor, hasNextPage, loading, error, fetchMore, contentId, hasFullyLoaded } =
		useAccessRequests(fetchPolicy, 'accessRequestsTable');

	const [, setChanges] = useState<
		Map<string, { requestId: string; newAccessType: string; originalAccessType: string }>
	>(new Map());

	// Track changes internally
	const trackChange = useCallback(
		(requestId: string, newAccessType: string, originalAccessType: string) => {
			setChanges((prevChanges) => {
				const newChanges = new Map(prevChanges);
				if (newAccessType === originalAccessType) {
					newChanges.delete(requestId);
				} else {
					newChanges.set(requestId, { requestId, newAccessType, originalAccessType });
				}
				onHasUnsavedChanges?.(newChanges.size > 0);
				return newChanges;
			});
		},
		[onHasUnsavedChanges],
	);

	const clearChanges = useCallback(() => {
		setChanges(new Map());
		onHasUnsavedChanges?.(false);
	}, [onHasUnsavedChanges]);

	// Preserve scroll position when more entries are appended at the bottom of the scrollable container
	const containerRef = useRef<HTMLDivElement>(null);
	const scrollTopRef = useRef<number>(0);
	const isScrolled = useIsScrolled(containerRef);

	const totalResults = requests.length;

	const fetchNextPage = async () => {
		if (!hasNextPage) return;

		await fetchMore({
			variables: { after: endCursor },
			updateQuery: (previousResult, { fetchMoreResult }) => {
				// Save the scroll position of the container just before rendering the new results
				if (containerRef.current?.scrollTop) {
					scrollTopRef.current = containerRef.current.scrollTop;
				}

				// If there's no new data, return the previous result
				if (!fetchMoreResult) {
					return previousResult;
				}

				const previousNodes = previousResult.confluence_contentAccessRequestByStatus?.nodes || [];
				const newNodes = fetchMoreResult.confluence_contentAccessRequestByStatus?.nodes || [];
				const nodes = [...previousNodes, ...newNodes];

				const latestPageInfo = fetchMoreResult.confluence_contentAccessRequestByStatus?.pageInfo;
				const pageInfo: typeof latestPageInfo = {
					endCursor: latestPageInfo?.endCursor || null,
					startCursor: latestPageInfo?.startCursor || null,
					hasNextPage: latestPageInfo?.hasNextPage || false,
					__typename: latestPageInfo?.__typename || 'PageInfo',
				};

				return {
					...previousResult,
					confluence_contentAccessRequestByStatus: {
						...previousResult.confluence_contentAccessRequestByStatus,
						nodes,
						pageInfo,
					},
				};
			},
		});
	};

	useLayoutEffect(() => {
		// Apply the last saved scroll position of the container back to it when new results show up
		// Context: scrollTop gets reset when more entries are added
		// therefore we need to set it to whatever it was just before the entries were added
		if (containerRef.current && scrollTopRef.current) {
			containerRef.current.scrollTop = scrollTopRef.current;
		}
	}, [totalResults]);

	const [initialLoadComplete, setInitialLoadComplete] = useState(false);
	useEffect(() => {
		if (hasFullyLoaded) setInitialLoadComplete(true);
	}, [hasFullyLoaded]);

	const loadingHeight = initialLoadComplete ? 'initial' : `${minLoadingHeight}px`;
	const minTableHeight = minLoadingHeight;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (initialLoadComplete) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'viewed',
					actionSubject: 'accessRequestsTable',
					source,
					attributes: {
						count: getBucketedCount(totalResults),
						contentId,
					},
				},
			}).fire();
		}
	}, [initialLoadComplete, createAnalyticsEvent, totalResults, contentId, source]);

	if (error) {
		return (
			<div
				css={accessRequestsContainerStyles}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={{ height: loadingHeight, minHeight: minTableHeight }}
			>
				<AccessRequestsTableState
					textLine={formatMessage(i18n.errorFirstLine)}
					secondTextLine={formatMessage(i18n.errorSecondLine)}
					srcLight={LightError}
					srcDark={DarkError}
				/>
			</div>
		);
	}

	return (
		<div
			css={accessRequestsContainerStyles}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ height: loadingHeight, minHeight: minTableHeight }}
		>
			<Box xcss={[borderDummyStyles, isScrolled && tableBorderTop]} />
			<Stack xcss={[tableStyles]} ref={containerRef} as="ul" testId="access-requests-table">
				{!initialLoadComplete && (
					<Inline alignInline="center" alignBlock="center" xcss={spinnerContainerStyles}>
						<Spinner size="large" testId="access-requests-table-spinner" />
					</Inline>
				)}
				{initialLoadComplete && !loading && totalResults === 0 && (
					<AccessRequestsTableState
						textLine={formatMessage(i18n.emptyStateTitle)}
						srcLight={LightEmptyState}
						srcDark={DarkEmptyState}
					/>
				)}
				{initialLoadComplete && totalResults > 0 && (
					<StaggeredEntrance>
						{requests.filter(isDefined).map((node) => (
							<AccessRequestsTableRow
								contentId={contentId}
								accessRequest={node}
								key={node.id}
								isBlockedAccessFlowActive={isBlockedAccessFlowActive}
								maybeStartBlockedAccessFlowForUser={maybeStartBlockedAccessFlowForUser}
								source={source}
								shouldBlockRowProcessing={shouldBlockRowProcessing}
								onRowProcessingStart={onRowProcessingStart}
								onRowProcessingEnd={onRowProcessingEnd}
								trackChange={trackChange}
								clearChanges={clearChanges}
							/>
						))}
					</StaggeredEntrance>
				)}
				{hasNextPage ? (
					<ObservableSpinner
						onScrollTo={fetchNextPage}
						testId="access-requests-table-paginating-spinner"
						cursor={endCursor}
						observerOptions={{
							root: containerRef.current,
							rootMargin: '100px 0px 0px 0px',
						}}
					/>
				) : (
					initialLoadComplete && <Box xcss={paddingBoxStyles} />
				)}
			</Stack>
		</div>
	);
};
